import { useState, useEffect } from 'react';
import adminApiService from '../services/adminApiService';
import { AdminPermissionResponse, AdminUserInfoResponse } from '../types';

interface UseAdminPermissionState {
  loading: boolean;
  error: string | null;
  permission: AdminPermissionResponse | null;
  userInfo: AdminUserInfoResponse | null;
  hasAccess: boolean;
}

interface UseAdminPermissionResult extends UseAdminPermissionState {
  checkPermission: () => Promise<void>;
  getUserInfo: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook untuk mengelola pengecekan admin permission
 * Menyediakan state dan actions untuk check admin access
 */
export const useAdminPermission = (): UseAdminPermissionResult => {
  const [state, setState] = useState<UseAdminPermissionState>({
    loading: false,
    error: null,
    permission: null,
    userInfo: null,
    hasAccess: false,
  });

  const checkPermission = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const permission = await adminApiService.checkAdminPermission();
      setState(prev => ({ 
        ...prev, 
        permission, 
        hasAccess: permission.has_admin_access,
        loading: false 
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check admin permission';
      setState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        hasAccess: false,
        loading: false 
      }));
    }
  };

  const getUserInfo = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const userInfo = await adminApiService.getAdminUserInfo();
      setState(prev => ({ 
        ...prev, 
        userInfo, 
        hasAccess: userInfo.has_admin_access,
        loading: false 
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get admin user info';
      setState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        hasAccess: false,
        loading: false 
      }));
    }
  };

  const refresh = async () => {
    await Promise.allSettled([checkPermission(), getUserInfo()]);
  };

  // Auto check permission on mount
  useEffect(() => {
    checkPermission();
  }, []);

  return {
    ...state,
    checkPermission,
    getUserInfo,
    refresh,
  };
};

export default useAdminPermission;
